import { chatThreadsService } from "@/services/chatThreadsService";
import type { SerializedChatMessage } from "@/lib/pendingThreadStore";
import {
  describePersistMismatch,
  evaluatePersistMatch,
  fingerprintMessages,
  messagesMatchDbForPhase,
  type PersistPhase,
} from "@/lib/threadPersistMatch";

export type { PersistPhase } from "@/lib/threadPersistMatch";
export {
  describePersistMismatch,
  evaluatePersistMatch,
  fingerprintMessages,
  messagesMatchDb,
  messagesMatchDbForPhase,
  resolvePersistPhase,
  turnIsComplete,
} from "@/lib/threadPersistMatch";

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 400;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export type VerifyThreadResult = {
  verified: boolean;
  /** IDs confirmed in DB but phase gate not satisfied (incomplete turn). */
  syncedPartial?: boolean;
  error?: string;
};

function evaluateLocalAgainstDb(
  localMessages: SerializedChatMessage[],
  dbMessages: unknown,
  phase: PersistPhase
): VerifyThreadResult {
  const check = evaluatePersistMatch(localMessages, dbMessages, phase);
  if (check.idsMatch && check.phaseOk) {
    return { verified: true };
  }
  if (check.idsMatch) {
    // Content is confirmed saved; turn is incomplete for this phase.
    return { verified: false, syncedPartial: true };
  }
  return {
    verified: false,
    error: describePersistMismatch(localMessages, dbMessages, phase, check),
  };
}

export async function verifyThreadInDatabase(
  threadId: string,
  localMessages: SerializedChatMessage[],
  workspaceId?: string | null,
  spaceContextId?: string | null,
  phase: PersistPhase = "final"
): Promise<VerifyThreadResult> {
  try {
    const row = await chatThreadsService.getChatThread(
      threadId,
      workspaceId ?? null,
      spaceContextId ?? null
    );
    if (!row) {
      return { verified: false, error: "Thread not found in database after save." };
    }
    return evaluateLocalAgainstDb(localMessages, row.messages, phase);
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Verification read failed.";
    return { verified: false, error: msg };
  }
}

export type PersistThreadWriteInput = {
  threadId: string | null;
  userId: string;
  messages: SerializedChatMessage[];
  linkedSpaceIds: string[];
  attachedGitHubRepos: Array<{ fullName: string; owner: string; name: string }>;
  contextMode: string;
  chatMode: string;
  agentId: string | null;
  topicId: string | null;
  workspaceId: string | null;
  spaceContextId?: string | null;
  skipAutoTitle?: boolean;
  phase?: PersistPhase;
};

export type PersistAndVerifyResult = {
  verified: boolean;
  /** IDs confirmed in DB but turn incomplete — use synced_partial status. */
  syncedPartial?: boolean;
  threadId: string | null;
  error?: string;
  thread?: { id: string; title?: string | null; messages?: unknown[] };
};

async function writeThreadOnce(input: PersistThreadWriteInput): Promise<{
  threadId: string;
  thread: { id: string; title?: string | null; messages?: unknown[] };
}> {
  const {
    threadId,
    userId,
    messages,
    linkedSpaceIds,
    attachedGitHubRepos,
    contextMode,
    chatMode,
    agentId,
    topicId,
    workspaceId,
  } = input;

  if (!threadId) {
    const created = await chatThreadsService.createChatThread(
      {
        userId,
        messages,
        linkedSpaceIds,
        attachedGitHubRepos: attachedGitHubRepos.length ? attachedGitHubRepos : [],
        contextMode,
        chatMode,
        agentId,
        topicId,
        title: null,
        status: "active",
      },
      workspaceId
    );
    return { threadId: String(created.id), thread: created };
  }

  const updated = await chatThreadsService.updateChatThread(
    threadId,
    { messages },
    workspaceId,
    input.spaceContextId ?? null
  );
  if (!updated) {
    throw new Error(
      "Update returned no row — thread may be missing, or workspace/ownership does not match."
    );
  }
  return {
    threadId,
    thread: updated,
  };
}

/** Retry write + verification until messages match or retries exhausted. */
export async function persistAndVerifyThread(
  input: PersistThreadWriteInput
): Promise<PersistAndVerifyResult> {
  const phase = input.phase ?? "final";
  let lastError = "Unknown persist error";
  let lastThreadId = input.threadId;
  let lastSyncedPartial = false;
  let lastThread: PersistAndVerifyResult["thread"];

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await delay(BASE_DELAY_MS * Math.pow(2, attempt - 1));
    }
    try {
      const { threadId, thread } = await writeThreadOnce({
        ...input,
        threadId: lastThreadId,
      });
      lastThreadId = threadId;
      lastThread = thread;

      let verification: VerifyThreadResult;
      if (Array.isArray(thread.messages)) {
        verification = evaluateLocalAgainstDb(input.messages, thread.messages, phase);
      } else {
        verification = await verifyThreadInDatabase(
          threadId,
          input.messages,
          input.workspaceId,
          input.spaceContextId,
          phase
        );
      }

      if (verification.verified) {
        return { verified: true, threadId, thread };
      }
      if (verification.syncedPartial) {
        lastSyncedPartial = true;
        // Content is saved; no need to keep retrying for phase mismatch.
        return {
          verified: false,
          syncedPartial: true,
          threadId,
          thread,
        };
      }
      lastError = verification.error ?? "Verification failed.";
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
      lastSyncedPartial = false;
    }
  }

  return {
    verified: false,
    syncedPartial: lastSyncedPartial,
    threadId: lastThreadId,
    error: lastError,
    thread: lastThread,
  };
}
